import { StrictMode } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { getAccessToken } from "@/auth";
import { AppProviders } from "@/app-providers";
import { initializeEmbeddedPublicRouteData, preloadPublicBootstrap, preloadTourStopBySlug } from "@/lib/data/repo";
import App, { preloadPublicPages, preloadWorkspacePages } from "@/App";
import "@/globals.css";

// Google OAuth treats localhost and 127.0.0.1 as different JavaScript
// origins. Keep local development on the registered canonical origin.
if (import.meta.env.DEV && window.location.hostname === "127.0.0.1") {
  const canonicalUrl = new URL(window.location.href);
  canonicalUrl.hostname = "localhost";
  window.location.replace(canonicalUrl);
} else {
  const catalogRoutes = new Set(["/", "/music", "/sports", "/culture", "/community", "/profiles", "/cities"]);
  if (catalogRoutes.has(window.location.pathname)) {
    void preloadPublicBootstrap().catch(() => undefined);
  }
  const eventMatch = window.location.pathname.match(/^\/events\/([^/]+)\/?$/);
  const hasPrerenderedEvent = document.getElementById("root")?.dataset.edagaPrerendered === "event";
  if (eventMatch && !hasPrerenderedEvent) {
    void preloadTourStopBySlug(decodeURIComponent(eventMatch[1])).catch(() => undefined);
  }
  if (getAccessToken() && /^(\/dashboard(?:\/|$)|\/tickets(?:\/|$)|\/orders(?:\/|$)|\/submit-event\/?$|\/for-promoters\/?$|\/promoter(?:\/|$)|\/scan\/?$|\/preview(?:\/|$)|\/admin(?:\/|$))/.test(window.location.pathname)) {
    void preloadWorkspacePages().catch(() => undefined);
  }
  const root = document.getElementById("root")!;
  const prerendered = root.dataset.edagaPrerendered === "event";
  if (prerendered) initializeEmbeddedPublicRouteData();
  const application = (
    <StrictMode>
      <BrowserRouter>
        <AppProviders initialLocale={prerendered ? "en" : undefined} deferStoredSession={prerendered}>
          <App />
        </AppProviders>
      </BrowserRouter>
    </StrictMode>
  );
  if (prerendered) void preloadPublicPages().then(() => hydrateRoot(root, application));
  else createRoot(root).render(application);
}
